/**
 * 이상한 방 - 게임 HUD (수색 루프 전용 레이어 · 순수 DOM)
 *
 * 근거 문서: 컨셉시트 3절(라운드 구조) · 4.3절(탄환 경제) · 4.4절(정답 방) · 5절(웃김 설계)
 *           상태모델 6절(판정) · 워프 규칙(`session.ts` 머리말)
 *
 * ## `hud.ts`와 왜 파일을 나눴나
 *
 * 기존 `hud.ts`는 **배치 모드**의 HUD다 - 카탈로그·팔레트·셀 좌표. 수색 모드가 필요로 하는 것은
 * 전혀 다르다 - 페이즈 시계·발견 수·탄환·정답 방. 둘을 한 파일에 넣으면 `render(view)` 하나가
 * 두 모드의 상태를 전부 받아야 하고, 그 뷰 타입은 절반이 항상 `null`인 물건이 된다.
 * 레이어를 나누면 모드 전환이 **루트 하나를 껐다 켜는 일**로 끝난다(`setVisible`).
 *
 * ## 이 HUD의 유일한 설계 제약: 화면 중앙을 비운다
 *
 * 이 게임에서 UI가 먹는 면적은 곧 **못 보는 방의 넓이**다. 변화를 찾는 게임에서 그건
 * 미관이 아니라 공정성 문제다(상태모델 원칙 2). 그래서 모든 것이 네 귀퉁이와 위아래 가장자리에 있고,
 * 정보 패널은 전부 `pointer-events: none`이라 그 위를 탭해도 **방을 향해 총이 나간다.**
 * 포인터를 먹는 것은 실제로 눌러야 하는 버튼 둘(건너뛰기·정답 방)뿐이다.
 *
 * ## 왜 뷰 모델을 받는가 (GameState를 직접 안 받는 이유)
 *
 * HUD가 `GameState`를 알면 상태머신의 필드 이름 변경이 곧 UI 컴파일 에러가 되고,
 * 반대로 HUD를 테스트하려면 방 생성기까지 돌려야 한다. 그래서 **평평한 뷰 모델**만 받는다.
 * 통합 담당이 셀렉터(`phaseRemainingMs` 등)로 15줄쯤 매핑하는 비용을 치르는 대신,
 * 이 파일은 게임 규칙을 한 줄도 갖지 않는다 - 규칙은 전부 `gameState.ts`/`hunt.ts`에 있다.
 *
 * ## 갱신 빈도 가정
 *
 * `render`는 **매 프레임 호출된다고 가정한다**(시계가 1초마다 바뀌려면 그래야 한다).
 * 그래서 모든 DOM 쓰기는 "값이 같으면 건드리지 않는다"로 감싼다. 방 안에 소품이 391개인데
 * HUD가 매 프레임 레이아웃을 재계산시키면 그 비용은 곧 프레임 드랍이고,
 * 프레임이 끊기면 "움직임처럼 튀어나오는" 변화 감지 자체가 망가진다.
 *
 * ## 문구는 하나도 이 파일에 없다
 *
 * 모든 표시 문구는 `i18n`의 `t()`에서 나온다. 사전 키는 열거형 값에 맞춰 조립한다
 * (`phase.${phase}.name`, `shot.${kind}.label`) - 여기에 분기문을 새로 만들지 마라.
 *
 * `render` 경로에서 `t()`를 매 프레임 부르는 것은 의도다. 사전 조회는 맵 하나 + 치환 한 번이고,
 * 반환값이 같으면 `setText`가 DOM을 건드리지 않으므로 프레임 예산에 닿는 것은 없다.
 * 대신 **언어를 바꾸면 다음 프레임에 화면이 저절로 따라온다** - 갱신 경로를 따로 만들 필요가 없다.
 * 프레임마다 다시 그려지지 않는 것(라벨·워터마크·배지)만 `paintStatic`이 맡고,
 * 그것만 `onLocaleChange`로 다시 칠한다.
 *
 * ## 보급이 방에서 계정으로 옮겨간 뒤 (2026-09-02)
 *
 * 탄환을 채우는 것은 이제 방 바닥의 아이템이 아니라 **계정이 들고 있는 탄창**이다(`magazines.ts`).
 * 주우러 가는 동선이 사라진 자리에 버튼 하나가 들어왔고, 이 파일이 바뀐 것은 넷이다.
 *
 *  1. **탄창 칩**: 큰 숫자 오른쪽에 보유량과 버튼이 붙는다(`createMagChip`). 열리는 인벤토리가
 *     아니라 **계기판의 수치 표시**다 - 자리를 옮기지 않고, 펼쳐지지 않고, 방을 가리지 않는다.
 *  2. **경고의 뜻이 갈렸다**: 탄환 0이 더 이상 한 가지 사건이 아니다. 쓸 탄창이 있으면
 *     그건 "쓸 때"(노랑)이고, 없을 때만 "이번 판은 끝"(빨강)이다(`ammoTier`).
 *  3. **재장전 연출**: `+6발` 자막 + 큰 숫자 튕김 + 탄피 줄이 왼쪽부터 차오른다.
 *     셋 다 0.5초 안에 끝난다(`AMMO_GAIN_DURATION_MS`). 이벤트가 아니라 뷰의 변화에서
 *     파생시킨다(`ammoGainSince` 주석 - 효과를 받는 파일이 남의 소유다).
 *  4. **짧은 쿨타임의 얼굴**은 그대로다: 5초에 큰 숫자 카운트다운은 기다림이 아니라
 *     깜빡임이라 버튼의 이름을 고정하고 게이지에게 시간을 맡긴다(`BRIEF_COOLDOWN_MS`).
 *
 * 밸런스 전제를 화면도 지킨다: **탄창은 없어도 모든 단계를 클리어할 수 있는 보조 자원**이다
 * (곡선은 `levels.ts`가 보장한다). 그래서 보유량 0인 칩은 채근하지 않는다 -
 * 0의 얼굴은 경고가 아니라 조용한 비활성이다. 여기서 빨간색을 쓰면 화면이 "돈을 내라"고 말하게 된다.
 *
 * ## 표식의 뜻이 방송에서 현장 감식으로 바뀌었다 (2026-09-02)
 *
 * `REC`·`PLAYBACK`·`CAM 01`은 이 게임의 주제와 이어지지 않았다. 컨셉이 **"누가 다녀갔다"**로
 * 다시 읽히면서 같은 자리의 표식이 `현장`·`원본`·현장 번호가 됐다(`i18n/ko.ts`의 감식 어휘 절).
 *
 * **모양은 한 픽셀도 바뀌지 않았다.** 붉은 점은 그대로 깜빡이고, 원본 방에서는 그대로 ◂ 삼각형이
 * 되며, 워터마크는 그대로 하단 가운데에 있다. CSS 클래스 이름(`gh-phase__rec`·`gh-cam`)도 그대로다 -
 * 화면에 안 보이는 이름을 바꾸면 변경 범위만 커지고 얻는 것이 없다. 바뀐 것은 **글자와 그 뜻**뿐이다.
 */

import { ceilSeconds, MS_PER_SECOND } from '../core/time'
import type { Phase } from '../game/gameState'
import type { OriginRoomStatus } from '../game/originVisit'
import { hasKey, onLocaleChange, t } from '../i18n'
import { zoneName } from '../i18n/zoneName'

import { createElement, restartAnimation, setAttr, setBarRatio, setFlag, setHidden, setText } from './dom'

/**
 * 배수 표기(`×1.4`)를 연출 레이어에서 가져온다. 같은 순간에 두 곳이 같은 숫자를 그린다 -
 * 맞은 지점의 배수 칩(`juice.ts`)과 여기 상단의 콤보 칩이다. 표기가 한 글자라도 갈리면
 * 사람은 둘이 다른 값이라고 읽고, 그 순간 점수판 전체를 의심한다.
 */
import { formatMultiplier } from './juice'
/*
 * 걸기 칩(M7). 콤보 칩 옆에 서는 단추 하나이고 **로직은 전부 `raiseChip.ts`에 있다** - 이 파일은 마운트 지점과 뷰 두 값의
 * 배선만 갖는다(1200줄대 파일에 더 얹지 않는다). 잠긴 칩을 눌렀을 때의 이유는 이 HUD의 토스트로 나간다(`onLocked`).
 */
import { overlayLayout, publishSize, type SizePublisher } from './overlayLayout'
import { createRaiseChip, type RaiseChipHandle, type RaiseLockReason } from './raiseChip'

import './gameHud.css'

/* --- 튜닝되지 않는 표시 상수 -------------------------------------------------- */
/*
 * 여기 있는 값은 **게임 규칙이 아니라 연출 길이·연출 문턱**이다. 규칙 수치(탄환 12발·
 * 쿨타임 5초·경고 2발 등)는 전부 `game/tuning.ts`가 원본이며 이 파일은 그것을 뷰로 받아 그린다.
 * 둘을 섞으면 "탄환 수를 바꾸려면 HUD도 고쳐야 하는" 상태가 되고, 그 순간 A/B가 불가능해진다.
 *
 * 문턱 상수(`URGENT_PHASE_MS`·`AMMO_CAUTION_RATIO`·`BRIEF_COOLDOWN_MS`)가 여기 있어도 되는
 * 이유는 그것들이 **플레이를 바꾸지 않기 때문**이다. 언제 붉어지는지·언제 초를 세는지는
 * 규칙이 아니라 규칙을 읽는 방법이고, 셋 다 튜닝 값을 입력으로 받아 비율·길이로만 판단한다.
 */

/** 토스트 표시 시간(ms). 수색이 최대 5분인데 문구가 오래 남으면 그 자체가 시야 방해다. */
const NOTICE_DURATION_MS = 2000

/** 히트마커가 화면에 남는 시간(ms). 길면 다음 사격의 표식과 겹쳐 몇 발이 맞았는지 알 수 없다 - 항상 마지막 한 발만 보인다. */
const SHOT_MARK_DURATION_MS = 520

/** 오답 시 화면 가장자리 붉은 플래시 길이(ms). 중앙을 건드리지 않고 귀책을 알리는 장치다. */
const MISS_FLASH_DURATION_MS = 380

/**
 * 페이즈 시계가 경고색으로 바뀌는 잔여 시간(ms).
 * 시간이 "줄어드는 게 보여야" 긴장이 생기는데, 숫자만으로는 곁눈질로 안 읽힌다.
 */
const URGENT_PHASE_MS = 30 * MS_PER_SECOND

/**
 * 정답 방 체류가 다급해지는 잔여 시간(ms).
 * 컨셉시트 5절의 "빨리 나와!!"를 싱글에서 대신하는 것이 이 카운트다운이다.
 */
const URGENT_STAY_MS = 5 * MS_PER_SECOND

/**
 * 재장전 자막(`+6발`)과 탄피 줄 차오름의 수명(ms).
 *
 * **0.5초 안에 사라져야 한다는 것이 이 값의 유일한 요구다.** 재장전은 라운드에 두 번까지
 * 일어나는 잔일이라, 발견 자막만큼 오래 남으면 화면 왼쪽 아래에 글자가 상주하는 상태가 된다.
 * 그리고 이 자막이 하는 말은 이미 큰 숫자가 하고 있다 - 자막의 역할은 "방금 늘었다"는
 * 시점 표시뿐이므로, 눈이 한 번 붙었다 떨어질 만큼만 있으면 된다.
 *
 * 탄피 줄의 차오름도 이 안에서 끝나야 한다. 마지막 칸의 지연 + 길이가 이 값을 넘으면
 * 정리 타이머가 애니메이션을 중간에 자른다(CSS의 `gh-pip-refill` 옆 계산 참고).
 */
const AMMO_GAIN_DURATION_MS = 460

/**
 * 탄피 표식의 최대 개수. 넘으면 표식을 통째로 감춘다(`syncPips` 호출부).
 *
 * 상한이 필요한 이유는 폭이다 - 12칸이 176px이고 그 오른쪽에 정답 방 버튼이 있어
 * 375px 폭에서 이미 꽉 찬다. 잘라서 그리지 않고 감추는 이유는 정직함이다:
 * `?ammo=20`인데 12칸이 전부 차 있으면 그 줄은 곁눈질에게 거짓말을 한다.
 * 큰 숫자가 언제나 정답이므로 애매한 줄보다 없는 줄이 낫다.
 *
 * 값을 16으로 올리지 않은 것은 의도다. 8~10단계(14·16발)와 자유 모드(16발)에서 이 줄은
 * 사라지며, `levels.ts`가 그 대가를 이미 적어 두고 곡선을 짰다. 칸을 16개로 늘리려면
 * 폰에서 칸 폭을 7px까지 깎아야 하는데, 그때 이 줄은 "셀 수 있는 탄피"가 아니라
 * 그냥 막대가 된다 - 곁눈질에 개수를 주려던 목적 자체가 사라진다.
 */
export const AMMO_PIP_MAX = 12

/**
 * 수색 중 커서. 아이소메트릭에는 크로스헤어가 없으므로 커서가 유일한 조준 신호다(`aim`). 원래 방에서는 쏠 것이 없으므로
 * (4.4절) 누르기 전에 그 사실이 보이게 한다(`blocked`). 두 세션(`game/session.ts` · `game/matchSession.ts`)이 이 값을
 * 가져다 쓴다 - 조준 신호가 판에 따라 다르면 손이 배운 것이 거짓말이 된다. HUD가 캔버스 커서를 직접 만지지는 않는다
 * (캔버스는 세션의 것) - 값의 원본이 계기판 옆에 있을 뿐이다.
 */
export const HUNT_CURSOR = { aim: 'crosshair', blocked: 'not-allowed' } as const

/**
 * 잔탄이 시작 탄환의 이 비율 이하로 떨어지면 **1단계 주의**로 넘어간다.
 *
 * ## 왜 단계를 하나 늘렸나
 *
 * `tuning.lowAmmoWarningAt`은 2발이고, 시작 탄환이 6발이던 시절 그것은 예산의 1/3이었다 -
 * 붉은 맥박이 도는 시점에 아직 세 번쯤 판단할 여유가 있었다. 시작 탄환이 16발이 되면서
 * 같은 2발이 예산의 1/8이 됐고, 경고가 **손쓸 수 없는 시점에 오는 경고**가 됐다.
 *
 * 값은 Core 담당 소유라 건드리지 않는다. 대신 잃어버린 그 여유를 표현으로 되돌린다.
 * 비율로 잡으므로 `?ammo=`를 어떻게 돌려도 "예산의 1/3"이라는 의미는 유지되고,
 * 시작 탄환이 6이던 옛 균형에서는 문턱이 2발 = 경고선과 겹쳐 **예전과 똑같이 동작한다**
 * (`isLowAmmo`가 먼저 이기므로 주의 단계가 나타나지 않는다).
 *
 * 두 단계는 채널이 다르다. 주의는 **색만**(자막 노랑), 경고는 **색 + 맥박**(경고 빨강).
 * 맥박을 주의에까지 주면 라운드의 절반이 깜빡이게 되고, 늘 깜빡이는 것은 경고가 아니다.
 */
const AMMO_CAUTION_RATIO = 1 / 3

/**
 * 이 길이 이하의 정답 방 쿨타임은 **초를 세지 않는다**(게이지만 채운다).
 *
 * 쿨타임이 30초일 때 큰 숫자 카운트다운은 기다림의 얼굴이었다. 5초에서 같은 표시는
 * 얼굴이 아니라 깜빡임이다 - 버튼의 대형 라벨이 `원래 방`→`5초 뒤`→`4초 뒤`…→`원래 방`으로
 * 몇 초마다 갈아치워지면, 화면 아래쪽에서 글자가 계속 바뀌는 것 자체가 방을 보는 눈을 끌어당긴다.
 * 게다가 5초짜리 숫자는 **버튼을 5초 동안 쳐다보게 만든다** - 그 5초는 방을 봐야 하는 시간이다.
 *
 * 그래서 짧은 쿨타임에서는 대형 라벨을 `원래 방`으로 **고정**하고(버튼의 이름은 바뀌지 않는다),
 * 차오르는 게이지와 흐려진 버튼이 상태를 말한다. 게이지는 연속값이라 곁눈질에 걸리고
 * 글자처럼 시선을 요구하지 않는다.
 *
 * 문턱을 두고 가르는 이유: `?cooldown=30`으로 되돌려 보는 실험이 계속 살아 있어야 한다.
 * 30초를 게이지만으로 기다리게 하면 "얼마나 남았나"를 물을 방법이 사라진다.
 */
const BRIEF_COOLDOWN_MS = 8 * MS_PER_SECOND

/**
 * 콤보 칩이 나타나는 최소 연속 수.
 *
 * 1연속의 배수는 1.0이라(`combo.ts`: "첫 발견은 1.0배다") 띄울 말이 없다. 그런데도 띄우면
 * 발견할 때마다 칩이 떴다 사라지기를 반복하고, 화면 오른쪽 위의 그 깜빡임이 곧 방을 훑는
 * 시선을 끌어당긴다. **칩이 뜨는 것 자체가 "지금 뭔가 쌓이고 있다"는 신호**여야 하므로
 * 실제로 값이 붙는 2연속부터 등장한다.
 */
const COMBO_CHIP_MIN = 2

/* --- 뷰 타입 ------------------------------------------------------------------ */

/**
 * 정답 방 버튼이 가질 수 있는 네 가지 얼굴. 타입의 원본은 `game/originVisit.ts`다 - 싱글 뷰와 매치 뷰가 같은 리프에서
 * 같은 얼굴을 만들고, 이 HUD는 두 뷰를 같은 손으로 그린다. 여기서 다시 export 하는 이유는 화면 모듈들이 뷰 타입을
 * HUD에서 가져가는 관례(`sessionView`·`resultScreen`)를 지키기 위해서다. 쿨타임 길이에 따라 표시 방법이 갈리는
 * 것(`BRIEF_COOLDOWN_MS`)은 이 파일의 결정이고, 그래서 `totalMs`가 필요한 이유도 그 상수 옆에 있다.
 */
export type { OriginRoomStatus }

/**
 * 한 발의 화면 표현.
 *
 * `object`/`removed`를 가르는 이유는 상태모델 6.1절이 히트 경로를 둘로 나누기 때문이다 -
 * "저 소파 이상해"와 "여기 뭐가 있었는데"는 다른 추론이고, 같은 표시를 내면
 * 자기가 무엇을 맞혔는지 알 수 없다. 색만이 아니라 **모양**도 다르게 준다(색약 대응).
 */
export type ShotMarkKind = 'object' | 'removed' | 'miss' | 'already-found' | 'dry' | 'pending'
/*
 * `pending`은 여럿이(매치)에서만 쓴다 - 판정이 호스트에서 오기 전까지의 **회색 하나**다(로드맵 2.5절 "낙관적 표식은
 * 회색 하나까지만"). 색이 있는 표식은 `shotResolved`가 도착한 뒤에만 찍힌다. 싱글은 같은 프레임에 판정이 끝나므로
 * 이 종류를 쓸 일이 없다.
 */

/**
 * HUD가 그릴 줄 아는 페이즈. 싱글의 넷(`gameState.Phase`)에 여럿이의 셋(`lobby`·`decorate`·`change`)이 붙었다 -
 * `game/match/matchTypes.MatchPhase`와 같은 값 집합이지만 **타입을 그쪽에서 가져오지 않는다.** HUD가 매치 타입에
 * 묶이면 리듀서 리팩터링 하나가 화면을 막는다(`resultScreen.RoundEndReason`이 사유를 직접 적는 것과 같은 결정).
 * 문구는 `phase.<p>.name`·`.hint`·`.skip` 조립 키 그대로다 - 여기에 분기문을 만들지 마라(머리말).
 */
export type HudPhase = Phase | 'lobby' | 'decorate' | 'change'

/**
 * `HudPhase`의 값 전부. `i18n/index.test.ts`가 이 목록으로 "조립 키가 두 사전에 있는가"를 돈다 -
 * 타입은 런타임에 열거할 수 없으므로 목록이 하나 더 필요하고, 그 목록이 타입과 갈리면 컴파일이 막힌다.
 * `satisfies readonly HudPhase[]`는 **원소가 타입 안에 있는가**만 보고 누락은 못 잡는다 - 그래서 아래 `MissingHudPhase`가
 * 빠진 값을 `never`가 아닌 타입으로 만들어 대입을 막는다. 페이즈 하나가 여기서 빠지면 i18n 검사가 그 페이즈를 조용히 건너뛴다.
 */
export const HUD_PHASES = ['lobby', 'decorate', 'watch', 'change', 'hunt', 'reveal', 'result'] as const satisfies readonly HudPhase[]
type MissingHudPhase = Exclude<HudPhase, (typeof HUD_PHASES)[number]>
type AssertNever<T extends never> = T
/** 목록에서 빠진 페이즈가 있으면 `MissingHudPhase`가 `never`가 아니게 되어 여기서 컴파일이 멈춘다(`matchTypes.PlayerEventTypesMatch`와 같은 장치). */
export type HudPhasesComplete = AssertNever<MissingHudPhase>

/** 토스트의 성격. 색과 테두리만 가른다. */
export type NoticeTone = 'info' | 'good' | 'warn'

/** 게임 HUD가 그리는 데 필요한 전부. 여기 없는 값은 HUD가 알 필요도 없는 값이다. */
export type GameHudView = Readonly<{
  phase: HudPhase
  /** 현재 페이즈 잔여 시간(ms). 결과 화면처럼 시간으로 끝나지 않는 페이즈는 `null`. */
  phaseRemainingMs: number | null
  /** 발견 수 / 전체 변화 수. 컨셉시트 3절의 조기 종료 조건이 이 둘의 일치다. */
  foundCount: number
  changeCount: number
  /**
   * 넘어가기 표(`ClientView.skip`). `needed`가 2 이상일 때만 세는 얼굴을 세운다 - 혼자인 판(싱글·이웃만 있는 판)에서
   * "1/1"은 세는 시늉일 뿐이다. 값은 **그대로 그린다** - 사람 좌석을 HUD가 다시 세면 정족수 규칙의 사본이 생긴다.
   */
  skip: Readonly<{ votes: number; needed: number; mine: boolean }>
  ammo: number
  /**
   * 시작 탄환 = **탄환 상한**(`gameState.ammoCapacity`) = 탄피 표식의 칸 수.
   * 정답 방 리필이 사라지면서 이 값을 넘는 잔탄은 더 이상 존재하지 않는다.
   */
  ammoMax: number
  /** `tuning.lowAmmoWarningAt` 이하인가. 문턱 판단은 규칙이므로 HUD가 다시 계산하지 않는다. */
  isLowAmmo: boolean
  /**
   * 계정이 들고 있는 탄창 수(`gameState.magazines`). **칩에 그대로 찍히는 숫자가 이것이다.**
   *
   * 라운드 값이 아니라 사람의 값이라 라운드가 끝나도 이어진다. 화면이 계정 보유량을 보여 주는
   * 이유는 이 자원의 성격 때문이다 - 광고·결제·미니게임으로 늘어날 물건이라면, 늘어나는 것을
   * 볼 자리가 라운드 안에 있어야 한다.
   */
  magazines: number
  /**
   * 이번 라운드에 아직 쓸 수 있는 수(`gameState.magazineUsesLeft` = 보유량과 라운드 상한의 작은 쪽).
   *
   * 표시하지 않는다. **왜 못 쓰는지를 가르는 데만 쓴다** - 보유량이 있는데 이 값이 0이면
   * 그건 "없다"가 아니라 "이번 판에는 다 썼다"이고, 두 상황에 같은 화면을 내면
   * 사람은 자기가 가진 것을 못 쓰는 이유를 영원히 알 수 없다.
   */
  magazineUsesLeft: number
  /**
   * 지금 누르면 실제로 채워지는가(`gameState.canUseMagazine`).
   *
   * 규칙 넷(수색 페이즈·잔탄 여유·보유량·라운드 상한)이 합쳐진 결과만 받는다.
   * HUD가 이 넷을 다시 계산하면 규칙이 두 벌이 되고, 그 순간 버튼의 얼굴과 실제 동작이 갈린다.
   */
  canUseMagazine: boolean
  /**
   * 탄창 하나가 채우는 양(`tuning.ammoPerMagazine`). **화면에 그리는 값이 아니라 거부 사유를
   * 숫자로 말하기 위한 값이다** - "몇 발을 더 써야 탄창이 들어가는가"가 여기서 나온다
   * (`onMagPress`의 `shotsUntilMagazineFits`).
   *
   * 상수 6을 이 파일에 박지 않는 이유는 다른 규칙 수치와 같다: 원본은 `tuning.ts` 하나이고
   * HUD는 그것을 뷰로 받는다. `?magAmmo=`로 회복량을 돌려 보는 실험도 그래야 화면까지 따라온다.
   *
   * **선택 항목인 이유는 소유 경계다.** 뷰를 짓는 곳(`game/session.ts`)이 다음 단계의 소유라
   * 여기서 필수로 만들면 그 한 줄이 배선되기 전까지 컴파일이 막힌다. 값이 없으면 문장이
   * 숫자를 잃을 뿐이고(`hud.mag.needRoom`) 거짓말은 하지 않는다.
   * 배선은 `ammoPerMagazine: tuning.ammoPerMagazine` 한 줄이다.
   */
  ammoPerMagazine?: number
  isInOriginRoom: boolean
  origin: OriginRoomStatus
  /**
   * 지금 이어지는 연속 정답 수(`gameState.currentCombo`). 0이면 콤보 없음.
   *
   * **HUD는 이 값에서 배수를 계산하지 않는다.** 배수 규칙(상한 3.0 포함)은 `combo.ts` 하나에만
   * 있어야 하고, 여기서 다시 곱하면 튜닝 한 번에 화면과 점수가 갈린다.
   */
  combo: number
  /** 지금 걸린 배수(`combo.comboMultiplier`). 표시 전용이며 점수에 곱하는 값이 아니다. */
  comboMultiplier: number
  /** 배수가 상한에 닿았는가(`combo.isComboAtCap`). 콤보 수는 계속 오르지만 배수는 멈춘다. */
  comboAtCap: boolean
  /**
   * 걸기(M7)를 선언했는가(`gameState.hasRaised` · 매치는 `self.raised`). 배수 칩이 두 겹이 되는 근거다.
   * 이 뷰가 규칙 몫에서 먼저 채워지고 칩은 걸기 UI 몫이 그린다 - 그때까지 HUD는 이 값을 읽지 않는다.
   */
  raised: boolean
  /**
   * 지금 걸 수 있는가(`gameState.canRaiseNow` · 매치는 `self.canRaise`). 조건 둘(잔탄 · 라운드 1회)은 규칙의 것이라
   * HUD가 `ammo >= 4`를 다시 적지 않는다 - `canUseMagazine`과 같은 규약이다.
   */
  canRaise: boolean
  /**
   * 걸기 칩을 세울 좌석인가(싱글은 언제나 참 · 매치는 `self.hunting` - 술래이고 관전이 아니다). `canRaise`가 거짓인 이유가
   * 잔탄이 아니라 **역할**인 사람(다녀간 사람 · 관전자)에게 칩을 세우면 잠긴 칩이 "탄환이 모자라다"고 거짓 이유를 말한다 -
   * 그 사람에게는 칩이 없어야 한다(GLOSSARY 3절 "왜 안 되는지는 반드시 말한다"의 이면 - 틀린 이유는 말하지 않는다).
   */
  raiseVisible: boolean
  /**
   * 내 구획의 id(여럿이 M11). 있으면 페이즈 머리에 이름표("내 구획 · 거실")가 선다 - 라운드 내내 서 있어야
   * "왜 저기는 못 쏘지"(내 구획의 이상한 곳은 내가 못 찾는다, 규칙 4)가 화면에서 설명된다.
   *
   * **선택 필드다.** 싱글 뷰(`sessionView.buildHudView`)는 이 값을 싣지 않고, 안 실으면 이름표가 서지 않는다 -
   * 필수로 두면 구획이 없는 싱글 경로가 `null`을 명시해야 하고, 그건 여럿이의 개념을 싱글 뷰에 심는 것이다.
   * 이름이 아니라 id인 이유는 `room.<id>` 조회처를 하나로 묶었기 때문이다(`i18n/zoneName.ts`).
   */
  myZoneId?: string | null
}>

/** HUD가 바깥으로 내보내는 유일한 출력. 전부 "사용자가 무엇을 눌렀다"뿐이다. */
export type GameHudHandlers = Readonly<{
  /** 정답 방 워프 토글. 입장 가부 판단은 상태머신의 몫이다. */
  onToggleOrigin: () => void
  /** 현재 페이즈를 즉시 끝낸다. 혼자 수십 판을 돌리는 이번 단계의 핵심 도구다. */
  onSkipPhase: () => void
  /**
   * 탄창 하나를 쓴다(`{ type: 'useMagazine' }`). **거부 판단은 여기서 하지 않는다** -
   * 눌렀으면 언제나 부른다. 못 쓰는 상황이면 리듀서가 막힌 소리를 내고, 화면 쪽 설명은
   * HUD가 뷰를 보고 따로 띄운다(`onMagPress`). 규칙과 설명을 같은 곳에서 판단하면
   * 둘이 갈리는 날이 오고, 그때 버튼은 "눌리는데 아무 일도 없는" 물건이 된다.
   */
  onUseMagazine: () => void
  /**
   * 걸기(M7, `{ type: 'raise' }`). 탄창과 같은 규약이다 - 걸 수 있는 칩을 눌렀을 때만 불리고(`raiseChip.ts`가 뷰로 가른다)
   * 잠긴 칩의 이유는 HUD가 토스트로 띄운다. 리듀서의 거부(경합)는 소리로 온다.
   */
  onRaise: () => void
}>

export type GameHudHandle = Readonly<{
  /**
   * HUD 루트. **연출 레이어가 붙잡을 손잡이다**(`juice.ts`의 `punchTarget`·`shakeTarget`).
   *
   * 씬 캔버스가 아니라 계기판을 흔드는 것이 이 게임의 규칙이다 - 캔버스에 CSS transform을 걸면
   * 렌더 해상도와 픽셀이 어긋나고, 무엇보다 방이 흔들리면 관찰이 방해받는다(원칙 2).
   * 밖으로 내주기만 하고 남이 무엇을 붙이는지는 알지 않는다.
   */
  root: HTMLElement
  render: (view: GameHudView) => void
  /**
   * 탭 지점에 사격 피드백을 띄운다. 화면 중앙이 아니라 **손가락이 닿은 곳**에 뜨는 이유는
   * 아이소메트릭에서 조준점이 곧 손가락이기 때문이다(중앙 크로스헤어는 이 게임에 없다).
   */
  flashShot: (clientX: number, clientY: number, kind: ShotMarkKind, label?: string) => void
  showNotice: (text: string, tone?: NoticeTone) => void
  /** 배치 모드로 전환할 때 레이어째 끈다. 진행 중이던 연출도 함께 정리된다. */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type GameHudOptions = Readonly<{
  mount: HTMLElement
  handlers: GameHudHandlers
  /**
   * 원본↔변경본 디졸브 길이(ms) = `tuning.warpDissolveSeconds * 1000`.
   * 화면 틴트가 씬 디졸브와 다른 속도로 들어오면 "지금 어느 방인가"가 그 사이 동안 모호해진다.
   */
  warpDissolveMs: number
}>

/* --- 문구 조회 ----------------------------------------------------------------- */

/*
 * 임시 사전은 사라졌다(2026-09-02).
 *
 * 콤보 칩·점수 내역의 문구가 한동안 이 파일 안의 표에 살았다 - `i18n/ko.ts`가 그 작업의
 * 소유가 아니어서 키를 직접 넣을 수 없었기 때문이다. 지금은 전부 사전에 있으므로
 * (`hud.combo.*` · `result.score.*` · `result.statCombo` · `result.comboValue` ·
 * `result.perfectBadge`) 이 파일에는 문구가 하나도 없고 `t()`만 부른다.
 *
 * **표를 되살리지 마라.** 사전 밖에 문구가 한 벌 더 생기는 순간 둘은 조용히 갈라지고,
 * 갈린 쪽은 번역에서 통째로 빠진다.
 */

/**
 * 페이즈별 문구. 키는 `Phase` 값 그대로 조립한다(`phase.hunt.name`).
 *
 * `skip`만 `hasKey`로 한 번 거른다. 건너뛰기 버튼이 있는 페이즈는 셋뿐이고
 * (외우기·수색·리빌) 결과 페이즈에는 그 키가 아예 없다 - "없는 문구"를 빈 문자열로 사전에
 * 적어 두면 채우다 만 항목과 구별되지 않는다. 버튼의 유무는 사전에 키가 있느냐로 정해진다.
 *
 * 같은 버튼이 페이즈마다 완전히 다른 일을 하므로 라벨도 다르다 -
 * 외우기에서는 "빨리 시작", 수색에서는 "포기".
 */
function phaseName(phase: HudPhase): string {
  return t(`phase.${phase}.name`)
}

function phaseHint(phase: HudPhase): string {
  return t(`phase.${phase}.hint`)
}

function phaseSkip(phase: HudPhase): string {
  const key = `phase.${phase}.skip`
  return hasKey(key) ? t(key) : ''
}

/* --- 시간 표기 ----------------------------------------------------------------- */

/**
 * `ms` → `M:SS`. 결과 화면도 같은 함수를 쓴다 -
 * 시계가 두 벌이면 "HUD는 3:42인데 결과는 3:41"이 나오고, 그 불일치는 곧 판정 불신이 된다.
 */
export function formatClock(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? ms : 0
  const totalSeconds = Math.ceil(safe / MS_PER_SECOND)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/** 카운트다운용 정수 초. 올림인 이유: "1초 남음"이 0으로 보이면 아직 있는 시간을 없다고 말하는 셈이다(`core/time.ceilSeconds`). */
const toSeconds = ceilSeconds

/* --- 부품 조립 ----------------------------------------------------------------- */

type PhaseBlock = Readonly<{
  root: HTMLElement
  /**
   * 지금 서 있는 곳의 표식(`현장` / `원본`). 클래스 이름은 방송 시절의 `rec` 그대로다 -
   * 화면에 안 보이는 이름이라 바꾸면 변경 범위만 늘어난다(파일 상단 "표식의 뜻" 절).
   */
  rec: HTMLElement
  name: HTMLElement
  /** 내 구획 이름표(여럿이 M11). 뷰에 `myZoneId`가 없거나 사전에 그 이름이 없으면 접힌다. */
  zone: HTMLElement
  clock: HTMLElement
  hint: HTMLElement
}>

function createPhaseBlock(): PhaseBlock {
  const root = createElement('div', 'gh-phase')
  const head = createElement('div', 'gh-phase__head')
  // 현장 표식. 원래 방에 들어가면 보고 있는 것이 현장이 아니라 원본이므로 render가 문구를 바꾼다.
  const rec = createElement('span', 'gh-phase__rec', t('hud.tag.scene'))
  const name = createElement('span', 'gh-phase__name', phaseName('watch'))
  // 내 구획 이름표(M11). 여럿이에만 서고 싱글에서는 통째로 접힌다(`GameHudView.myZoneId` 주석).
  const zone = createElement('span', 'gh-phase__zone', '')
  zone.hidden = true
  head.append(rec, name, zone)
  const clock = createElement('span', 'gh-phase__clock', formatClock(0))
  const hint = createElement('span', 'gh-phase__hint', phaseHint('watch'))
  root.append(head, clock, hint)
  return { root, rec, name, zone, clock, hint }
}

/**
 * 콤보 칩. **발견 카운터 바로 아래**에 붙는다.
 *
 * ## 왜 화면 중앙이 아닌가
 *
 * 배수가 오르는 것이 보여야 도파민이 산다는 것은 맞지만, 이 게임에서 UI가 덮은 면적은 곧
 * 볼 수 없는 방의 넓이다(원칙 2). 그래서 "오르는 순간"은 맞은 지점의 연출이 맡고
 * (`juice.ts`의 배수 칩 - 0.4초 안에 사라진다), 이 칩은 **지금 얼마가 걸려 있는지**를
 * 라운드 내내 조용히 들고 있는 상시 표시다. 같은 값을 두 곳이 말하지만 채널이 다르다 -
 * 하나는 사건이고 하나는 상태다.
 *
 * 자리가 발견 카운터 아래인 이유: 콤보는 발견의 성질이지 탄환의 성질이 아니다. 진행도 바로
 * 아래에 붙여 두면 "몇 개 찾았나"를 보는 눈이 배수까지 한 번에 읽는다.
 *
 * ## 사라질 때는 조용히 사라진다
 *
 * 콤보가 끊긴 것을 여기서 또 연출하지 않는다. 그 사건은 이미 맞은 지점에서 자막·플래시·
 * 흔들림 셋으로 말해지고(`juice.miss`), 같은 사건을 화면 네 번째 자리에서 반복하면
 * 자막이 겹치는 만큼 방이 가려진다. 칩이 그냥 없어지는 것이 곧 "잃었다"의 표시다.
 */
type ComboChip = Readonly<{
  root: HTMLElement
  label: HTMLElement
  value: HTMLElement
}>

function createComboChip(): ComboChip {
  const root = createElement('div', 'gh-combo')
  /*
   * 스크린리더에는 남기지 않는다. 발견 안내(`gh-sr`)가 한 발마다 이미 말하고 있어서,
   * 여기까지 읽으면 정답 한 발에 두 문장이 겹쳐 들린다. 배수는 점수의 근거이지
   * 다음 한 발을 정하는 데 필요한 정보가 아니다.
   */
  root.setAttribute('aria-hidden', 'true')
  const label = createElement('span', 'gh-combo__label', '')
  const value = createElement('span', 'gh-combo__value', '')
  root.append(label, value)
  return { root, label, value }
}

type FoundBlock = Readonly<{
  root: HTMLElement
  label: HTMLElement
  value: HTMLElement
  total: HTMLElement
  fill: HTMLElement
  combo: ComboChip
  /** 걸기 칩(M7). 콤보 칩과 한 줄에 선다 - 배수를 말하는 둘이 붙어 있어야 "이 둘이 곱해진다"가 설명 없이 읽힌다. */
  raise: RaiseChipHandle
}>

function createFoundBlock(onRaise: () => void, onRaiseLocked: (reason: RaiseLockReason) => void): FoundBlock {
  const root = createElement('div', 'gh-found')
  const row = createElement('div', 'gh-found__row')
  const value = createElement('span', 'gh-found__value', '0')
  /*
   * `hud.foundOf`("{found} / {total}")를 쓰지 않는 이유는 이 자리가 한 문자열이 아니기 때문이다.
   * 찾은 수는 40px 자막, 전체 수는 17px - 둘의 크기 차이가 곧 "얼마나 왔는가"의 신호라
   * 한 요소로 합치면 그 대비가 사라진다. 빗금은 문구가 아니라 두 수 사이의 기호다.
   */
  const total = createElement('span', 'gh-found__total', '/ 0')
  row.append(value, total)

  const bar = createElement('div', 'gh-bar')
  const fill = createElement('i', 'gh-bar__fill')
  bar.append(fill)

  const label = createElement('span', 'gh-found__label', '')
  const combo = createComboChip()
  const raise = createRaiseChip({ onPress: onRaise, onLocked: onRaiseLocked })
  /*
   * 칩은 **자리를 언제나 차지한다**(CSS가 투명도만 바꾼다). 흐름에서 넣었다 뺐다 하면
   * 콤보가 붙을 때마다 바로 아래의 건너뛰기 버튼이 위아래로 움직이고, 움직이는 버튼은
   * "눌렀는데 엉뚱한 게 눌리는" 최악의 실패로 가는 길이다. 걸기 칩도 같은 줄에서 같은 규약이다(`visibility`로만 숨는다).
   */
  const chips = createElement('div', 'gh-found__chips')
  chips.append(combo.root, raise.root)
  root.append(label, row, bar, chips)
  return { root, label, value, total, fill, combo, raise }
}

/**
 * 탄창 칩. **누를 수 있는 수치 표시**이지 인벤토리가 아니다.
 *
 * 개발자의 요구가 "인벤토리? 스킬? 같은 느낌으로 UI 한쪽에" 또는 "그냥 남은 탄창 숫자만
 * 표기"였고, 이 화면에서는 후자가 옳다 - 방을 덮는 서랍은 관찰 게임에서 그 자체로 해악이다.
 * 칸을 고르는 일이 존재하지 않는 자원이라(탄창은 개체가 아니라 개수다) 목록을 만들면
 * 고를 것 없는 목록이 된다.
 *
 * 자리는 큰 숫자 **바로 오른쪽**이다. 채워질 값과 채우는 버튼이 붙어 있어야
 * "이걸 누르면 저게 는다"가 설명 없이 읽히고, 탄피 줄보다 좁아 하단 줄의 폭도 늘지 않는다.
 * 아이콘·숫자·라벨 세 조각이며 라벨은 절대 바뀌지 않는다 - 버튼의 이름은 상태가 아니다.
 */
type MagChip = Readonly<{
  root: HTMLButtonElement
  count: HTMLElement
  label: HTMLElement
}>

function createMagChip(onPress: () => void): MagChip {
  const root = createElement('button', 'gh-mag')
  root.type = 'button'

  const row = createElement('span', 'gh-mag__row')
  // 탄창 모양은 CSS가 그린다(먹색 틈이 난 세로 막대). 뜻은 라벨이 말하므로 소리에서는 뺀다.
  const icon = createElement('span', 'gh-mag__icon')
  icon.setAttribute('aria-hidden', 'true')
  const count = createElement('span', 'gh-mag__count', '0')
  row.append(icon, count)

  const label = createElement('span', 'gh-mag__label', '')
  root.append(row, label)
  root.addEventListener('click', onPress)
  return { root, count, label }
}

type AmmoBlock = Readonly<{
  root: HTMLElement
  value: HTMLElement
  unit: HTMLElement
  pips: HTMLElement
  hint: HTMLElement
  gain: HTMLElement
  mag: MagChip
}>

function createAmmoBlock(onUseMagazine: () => void): AmmoBlock {
  const root = createElement('div', 'gh-ammo')
  const row = createElement('div', 'gh-ammo__row')
  const value = createElement('span', 'gh-ammo__value', '0')
  const unit = createElement('span', 'gh-ammo__unit', '')
  const mag = createMagChip(onUseMagazine)
  row.append(value, unit, mag.root)

  const pips = createElement('div', 'gh-ammo__pips')
  const hint = createElement('span', 'gh-ammo__hint', '')

  /*
   * 재장전 자막(`+6발`)은 **흐름에서 빠져 있다**(absolute). 줄에 끼워 넣으면 뜰 때마다
   * 탄환 블록의 폭이 넓어졌다 좁아지고, 화면 왼쪽 아래가 0.5초짜리 리플로를 반복한다.
   * 그 흔들림은 방을 훑는 시선이 가장 싫어하는 종류의 움직임이다.
   */
  const gain = createElement('span', 'gh-ammo__gain', '')
  gain.setAttribute('aria-hidden', 'true')

  root.append(gain, row, pips, hint)
  return { root, value, unit, pips, hint, gain, mag }
}

/**
 * 탄피 표식을 필요한 개수만큼만 만든다.
 *
 * 매번 비우고 다시 만들지 않는 이유는 애니메이션이다 - 잔탄 경고 맥박이 도는 중에
 * 노드를 갈아치우면 맥박이 매 프레임 처음부터 다시 시작해 정지한 것처럼 보인다.
 *
 * 칸마다 제 순번(`--gh-pip-index`)을 들려 보내는 이유는 재장전 연출이다. 왼쪽부터 차례로
 * 차올라야 "채워졌다"로 읽히는데, 그 시차를 CSS만으로 주려면 `nth-child` 규칙이 칸 수만큼
 * 필요하다. 순번은 만들 때 한 번만 쓰므로 매 프레임 비용이 아니다.
 */
function syncPips(container: HTMLElement, count: number, filled: number): void {
  while (container.childElementCount < count) {
    const pip = createElement('i', 'gh-ammo__pip')
    pip.style.setProperty('--gh-pip-index', String(container.childElementCount))
    container.append(pip)
  }
  while (container.childElementCount > count) {
    container.lastElementChild?.remove()
  }

  // `Array.from`은 프레임마다 배열을 새로 만든다. HTMLCollection은 인덱스로 바로 읽는다.
  for (let i = 0; i < container.children.length; i += 1) {
    container.children[i]?.classList.toggle('is-on', i < filled)
  }
}

type OriginButton = Readonly<{
  root: HTMLButtonElement
  label: HTMLElement
  sub: HTMLElement
  fill: HTMLElement
}>

function createOriginButton(onToggle: () => void): OriginButton {
  const root = createElement('button', 'gh-origin')
  root.type = 'button'

  // ◂◂ 되짚어 가는 표식(CSS가 그린다). 원본 방 배지와 같은 형태로 버튼과 화면 상태를 잇는다.
  const icon = createElement('span', 'gh-origin__icon')
  icon.setAttribute('aria-hidden', 'true')

  // 두 줄 다 render가 매 프레임 덮는다(상태에 따라 얼굴이 넷이다). 여기서는 비워 둔다.
  const label = createElement('span', 'gh-origin__label', '')
  const sub = createElement('span', 'gh-origin__sub', '')
  const bar = createElement('div', 'gh-bar gh-origin__bar')
  const fill = createElement('i', 'gh-bar__fill')
  bar.append(fill)

  root.append(icon, label, sub, bar)
  root.addEventListener('click', onToggle)
  return { root, label, sub, fill }
}

/* --- 정답 방 버튼의 얼굴 --------------------------------------------------------- */

type OriginFace = Readonly<{
  label: string
  sub: string
  ratio: number
  disabled: boolean
  hidden: boolean
  state: string
  urgent: boolean
  /** 짧은 쿨타임의 조용한 얼굴인가(`BRIEF_COOLDOWN_MS`). CSS가 흐림 정도를 낮춘다. */
  brief: boolean
}>

/**
 * 상태 → 버튼 표시. 순수 함수로 뽑아 둔 이유는 이 4갈래가 **규칙의 얼굴**이기 때문이다 -
 * "왜 안 눌리는지"를 화면이 설명하지 못하면 플레이어는 버튼이 고장 났다고 판단한다.
 */
function describeOrigin(status: OriginRoomStatus): OriginFace {
  switch (status.kind) {
    case 'ready':
      return {
        label: t('hud.origin.ready.label'),
        sub: t('hud.origin.ready.sub'),
        ratio: 1,
        disabled: false,
        hidden: false,
        state: 'is-ready',
        urgent: false,
        brief: false,
      }
    case 'staying': {
      const seconds = toSeconds(status.remainingMs)
      return {
        label: t('hud.origin.staying.label', { seconds }),
        sub: t('hud.origin.staying.sub'),
        ratio: status.totalMs > 0 ? status.remainingMs / status.totalMs : 0,
        disabled: false,
        hidden: false,
        state: 'is-staying',
        urgent: status.remainingMs <= URGENT_STAY_MS,
        brief: false,
      }
    }
    case 'cooldown': {
      /*
       * 짧은 쿨타임에서는 **버튼의 이름을 바꾸지 않는다.** 대형 라벨이 몇 초마다
       * `원래 방`↔`4초 뒤`로 갈아치워지면 그 자체가 화면 아래의 잔움직임이 된다.
       * 남은 시간은 게이지와 흐려짐이 말하고, 작은 줄이 "곧 열린다"로 마무리한다.
       * 긴 쿨타임(`?cooldown=30`)에서는 예전 그대로 초를 센다 - 그때는 숫자가 정보다.
       */
      const brief = status.totalMs > 0 && status.totalMs <= BRIEF_COOLDOWN_MS
      const seconds = toSeconds(status.remainingMs)
      return {
        label: brief
          ? t('hud.origin.ready.label')
          : t('hud.origin.cooldown.label', { seconds }),
        sub: brief ? t('hud.origin.cooldown.brief') : t('hud.origin.cooldown.sub'),
        // 남은 쿨타임이 아니라 **채워지는 방향**으로 그린다. 게이지가 차오르는 편이 기다림에 가깝다.
        ratio: status.totalMs > 0 ? 1 - status.remainingMs / status.totalMs : 1,
        disabled: true,
        hidden: false,
        state: 'is-cooldown',
        urgent: false,
        brief,
      }
    }
    case 'unavailable':
      return {
        // 버튼째 감추므로 이 문구는 화면에 닿지 않는다. 그래도 비워 두지는 않는다 -
        // 감추기가 한 번 어긋났을 때 빈 버튼이 뜨는 것보다 이름이 뜨는 편이 덜 이상하다.
        label: t('hud.origin.ready.label'),
        sub: '',
        ratio: 0,
        disabled: true,
        hidden: true,
        state: 'is-idle',
        urgent: false,
        brief: false,
      }
  }
}

const ORIGIN_STATES = ['is-ready', 'is-staying', 'is-cooldown', 'is-idle'] as const

/* --- 탄환 표시 단계 --------------------------------------------------------------- */

/** 탄환 블록이 가질 수 있는 네 얼굴. 색과 맥박만 가른다(숫자는 언제나 같은 자리에 있다). */
type AmmoTier = 'normal' | 'caution' | 'reload' | 'low'

const AMMO_TIERS = ['is-caution', 'is-reload', 'is-low'] as const

/**
 * 잔탄 → 표시 단계.
 *
 * ## 탄창이 생기면서 경고의 뜻이 갈렸다
 *
 * 전에는 잔탄이 마르는 것이 곧 라운드의 끝이라 붉은 맥박 하나면 됐다. 지금은 같은 잔탄 0이
 * 두 가지 사건이다 - **쓸 탄창이 있으면 그건 위험이 아니라 할 일이다.** 둘에 같은 빨강을
 * 쓰면 경고가 두 번 중 한 번은 거짓말을 하게 되고, 늘 거짓말하는 경고는 곧 무시된다.
 *
 *   normal   흰 숫자                      여유 있음
 *   caution  자막 노랑, 맥박 없음          예산 1/3 이하. "슬슬 채울 때다"
 *   reload   자막 노랑 + 칩이 맥박         마름 + 쓸 탄창 있음. 맥박은 **눌러야 할 것** 위에 있다
 *   low      경고 빨강 + 맥박              마름 + 쓸 탄창 없음. 이때만 진짜 위험이다
 *
 * `reload`에서 큰 숫자를 맥박시키지 않는 이유가 이 표의 요점이다. 급한 것은 잔탄이 아니라
 * "칩을 눌러라"이고, 알람은 해야 할 일 위에서 울려야 손이 그리로 간다.
 *
 * 시작 탄환이 6이던 옛 균형에서는 주의 문턱(6/3=2)과 경고선(2)이 겹치는데 마름을 먼저 보므로
 * **주의 단계가 아예 나타나지 않는다** - 즉 이 함수는 옛 값에서 옛 화면을 그대로 낸다.
 */
function ammoTier(view: GameHudView): AmmoTier {
  const dry = view.ammo <= 0 || view.isLowAmmo
  if (dry) return view.canUseMagazine ? 'reload' : 'low'
  if (view.ammo <= Math.floor(view.ammoMax * AMMO_CAUTION_RATIO)) return 'caution'
  return 'normal'
}

/* --- 탄창 칩의 얼굴 --------------------------------------------------------------- */

/**
 * 칩이 가질 수 있는 네 얼굴.
 *
 * `is-quiet`가 기본값인 것이 이 자원의 성격이다. 탄창 없이도 클리어할 수 있어야 하므로
 * 화면이 먼저 조르지 않는다 - 칩은 잔탄이 실제로 아쉬워졌을 때만 노란색을 켠다.
 */
const MAG_STATES = ['is-empty', 'is-quiet', 'is-ready', 'is-urgent'] as const
type MagState = (typeof MAG_STATES)[number]

function magState(view: GameHudView, tier: AmmoTier): MagState {
  // 보유량 0. 요구사항이 "비활성이 명확히 보여야 한다"이고, 그 얼굴은 경고가 아니라 침묵이다.
  if (view.magazines <= 0) return 'is-empty'
  // 총이 비었는데 채울 수 있다. 이 게임에서 칩이 시선을 요구해도 되는 유일한 순간이다.
  if (tier === 'reload') return view.ammo <= 0 ? 'is-urgent' : 'is-ready'
  if (tier === 'caution' && view.canUseMagazine) return 'is-ready'
  return 'is-quiet'
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 게임 HUD를 만들어 `mount`에 붙인다. 처음에는 **보이는 상태**이며,
 * 배치 모드로 시작하는 앱은 곧바로 `setVisible(false)`를 부르면 된다.
 */
export function createGameHud(options: GameHudOptions): GameHudHandle {
  const root = createElement('div', 'gh-root')
  root.style.setProperty('--gh-warp-ms', `${Math.max(0, options.warpDissolveMs)}ms`)
  // 보급 자막의 수명은 이 파일이 정한다. CSS가 숫자를 따로 들고 있으면 한쪽만 고치는 날이 온다.
  root.style.setProperty('--gh-ammo-gain-ms', `${AMMO_GAIN_DURATION_MS}ms`)

  // 원본 방 표식: 화면 전체 테두리 틴트. 이게 없으면 "지금 어느 방인가"가 헷갈리고,
  // 그 순간 게임이 성립하지 않는다(원본에는 변화가 없으므로 아무리 봐도 못 찾는다).
  const originFrame = createElement('div', 'gh-origin-frame')
  originFrame.setAttribute('aria-hidden', 'true')
  const originBadge = createElement('div', 'gh-origin-badge')
  const originBadgeText = createElement('span', 'gh-origin-badge__text', '')
  originBadge.append(createElement('span', 'gh-origin-badge__dot'), originBadgeText)

  const phase = createPhaseBlock()
  // 걸기 칩의 두 손잡이. 눌림은 세션으로, 잠긴 이유는 이 HUD의 토스트로(`raiseChip.ts` 머리말 - `disabled`가 아닌 이유).
  const found = createFoundBlock(
    () => options.handlers.onRaise(),
    (reason) => showNotice(t(`raise.lock.${reason}`), 'warn'),
  )
  const skip = createElement('button', 'gh-skip', phaseSkip('watch'))
  skip.type = 'button'
  skip.addEventListener('click', options.handlers.onSkipPhase)

  // 건너뛰기를 오른쪽 열에 두는 이유는 왼쪽 위가 이미 남의 자리이기 때문이다 -
  // `mobileControls.ts`의 모드 버튼이 그 좌표에 있고, 두 레이어의 마운트 순서를 여기서 가정할 수 없다.
  // 겹친 버튼은 "눌리기는 하는데 엉뚱한 게 눌리는" 최악의 실패를 낸다.
  const topRight = createElement('div', 'gh-top__right')
  topRight.append(found.root, skip)
  const top = createElement('header', 'gh-top')
  top.append(phase.root, topRight)

  // 화살표로 감싸는 이유는 순서다. 누를 때 필요한 것(마지막 뷰·토스트)이 아래에서 만들어지므로
  // 참조를 지금 굳히지 않고 누르는 순간 찾게 한다.
  const ammo = createAmmoBlock(() => onMagPress())
  const origin = createOriginButton(options.handlers.onToggleOrigin)
  const bottom = createElement('footer', 'gh-bottom')
  bottom.append(ammo.root, origin.root)

  // 현장 번호 워터마크. 픽션의 마침표이며 정보가 아니다 - 좁은 화면에서는 CSS가 먼저 버린다.
  // 클래스 이름은 방송 시절의 `gh-cam` 그대로다(파일 상단 "표식의 뜻" 절).
  const cam = createElement('div', 'gh-cam', '')
  cam.setAttribute('aria-hidden', 'true')

  const notice = createElement('div', 'gh-notice')
  notice.setAttribute('role', 'status')
  notice.setAttribute('aria-live', 'polite')

  // 히트마커는 하나만 두고 클래스로만 갈아 끼운다. 매 사격마다 만들면 첫 발에서
  // 스타일 계산이 끼어들어 연출이 한 프레임 늦게 시작된다(=맞았는지 애매해 보인다).
  const mark = createElement('div', 'gh-mark')
  mark.setAttribute('aria-hidden', 'true')
  const markLabel = createElement('span', 'gh-mark__label', '')
  mark.append(createElement('span', 'gh-mark__ring'), markLabel)

  // 오답 시 화면 **가장자리만** 붉게 번진다. 중앙을 덮으면 방이 안 보이고,
  // 방이 안 보이는 순간 다음 한 발의 판단 근거가 사라진다.
  const damage = createElement('div', 'gh-damage')
  damage.setAttribute('aria-hidden', 'true')

  // 사격 결과는 시각 연출이 전부라 스크린리더에는 아무것도 남지 않는다. 별도 라이브 리전으로 보낸다.
  const announcer = createElement('div', 'gh-sr')
  announcer.setAttribute('role', 'status')
  announcer.setAttribute('aria-live', 'polite')

  root.append(originFrame, top, originBadge, bottom, cam, mark, damage, announcer)
  options.mount.append(root)

  /*
   * 자막은 HUD 루트가 아니라 **위 기둥의 가운데 열**에 선다(`overlayLayout.ts`).
   * 전에는 연결 배너와 같은 좌표(`pad-top + 58px`)에 절대 위치로 서서 둘이 뜨면 글자가 그대로 포개졌다.
   * 루트 밖으로 나갔으므로 접기(`setVisible`)와 정리(`dispose`)에서 자막을 따로 챙긴다.
   */
  const layout = overlayLayout(options.mount)
  layout.place(notice, 'notice')

  /*
   * 이 HUD가 가진 상자의 크기를 내보낸다. 남들이 그 값을 손으로 베끼지 않게 하는 유일한 길이다:
   * 위 기둥이 상단 줄 높이를, 문 앞 줄이 원래 방 버튼 폭을, 바꾸기 띠·카메라 묶음이 하단 줄 높이를 읽는다.
   * 전에는 저쪽 파일들이 각각 `174px`·`76px`을 적어 두었고, 글꼴이나 언어가 바뀌면 곧바로 어긋났다.
   */
  const sizes: readonly SizePublisher[] = [
    publishSize(top, { height: '--gh-top-height' }),
    publishSize(bottom, { height: '--gh-bottom-height' }),
    publishSize(origin.root, { width: '--gh-origin-width' }),
  ]

  /**
   * 프레임마다 다시 그려지지 않는 문구만 여기서 칠한다.
   *
   * 나머지(페이즈·탄환 안내·정답 방 버튼)는 `render`가 매 프레임 사전에서 다시 꺼내므로
   * 언어가 바뀌면 다음 프레임에 저절로 갈린다. 이 넷은 그 경로에 없어서 따로 다시 칠해야 한다 -
   * 안 하면 언어를 바꿨을 때 화면 대부분은 영어인데 라벨 몇 개만 한국어로 남는다.
   */
  function paintStatic(): void {
    setText(found.label, t('hud.found'))
    setText(ammo.unit, t('hud.ammoUnit'))
    // 칩의 이름. 상태에 따라 바뀌지 않으므로 여기 있다 - 이름이 바뀌는 버튼은 다른 버튼으로 읽힌다.
    setText(ammo.mag.label, t('hud.mag.label'))
    setText(originBadgeText, t('hud.originBadge'))
    setText(cam, t('hud.tag.sceneNo'))
  }

  paintStatic()
  /**
   * 상단·하단 줄의 실측치를 다시 내보내야 하는가(`overlayLayout.ts`).
   *
   * **왜 곧바로 재지 않는가**: 언어가 갈리면 버튼의 문구가 길어져 줄의 높이가 바뀌는데, 그 문구는 `paintStatic`이 아니라
   * 다음 `render`가 칠한다("Original room / Compare with the original"은 세 줄이 된다). 그 자리에서 재면 **옛 문구의 높이**가
   * 내보내진다 - 실제로 영어에서 카메라 묶음이 원래 방 버튼 위로 4,435px² 내려앉았다.
   * 그래서 표시만 해 두고 다음 그리기의 끝에서 잰다.
   */
  let sizesDirty = true
  const stopLocaleWatch = onLocaleChange(() => {
    paintStatic()
    sizesDirty = true
  })

  // 타이머 핸들은 도메인 상태가 아니라 정리해야 할 자원이다.
  let noticeTimer: number | null = null
  let markTimer: number | null = null
  let damageTimer: number | null = null
  let gainTimer: number | null = null

  function clearGain(): void {
    if (gainTimer !== null) {
      clearTimeout(gainTimer)
      gainTimer = null
    }
    ammo.gain.classList.remove('is-on')
    ammo.value.classList.remove('is-bump')
    ammo.pips.classList.remove('is-refill')
  }

  function clearMark(): void {
    if (markTimer !== null) {
      clearTimeout(markTimer)
      markTimer = null
    }
    mark.className = 'gh-mark'
  }

  function clearDamage(): void {
    if (damageTimer !== null) {
      clearTimeout(damageTimer)
      damageTimer = null
    }
    damage.classList.remove('is-on')
  }

  function clearNotice(): void {
    if (noticeTimer !== null) {
      clearTimeout(noticeTimer)
      noticeTimer = null
    }
    notice.classList.remove('is-visible')
  }

  /** 바깥이 부르는 것과 칩이 부르는 것이 같은 토스트여야 한다 - 두 벌이면 둘이 겹쳐 뜬다. */
  function showNotice(text: string, tone: NoticeTone = 'info'): void {
    clearNotice()
    setText(notice, text)
    notice.dataset.tone = tone
    notice.classList.add('is-visible')
    noticeTimer = window.setTimeout(() => {
      notice.classList.remove('is-visible')
      noticeTimer = null
    }, NOTICE_DURATION_MS)
  }

  /**
   * 마지막으로 그린 뷰. 칩을 눌렀을 때 **왜 안 되는지**를 말하려면 그때의 수치가 필요하다.
   * 규칙을 다시 계산하지 않고 이미 받은 값만 읽는다 - 판단은 리듀서가 이미 끝냈다.
   */
  let lastView: GameHudView | null = null

  /**
   * 탄창이 통째로 들어가려면 **앞으로 몇 발을 더 써야 하는가**(`hasRoomForFullMagazine`).
   *
   * 회복량을 모르면 `null`이다 - 그때는 숫자 없는 문장으로 방향만 말한다.
   * 규칙을 다시 계산하는 것이 아니라 **거부당한 뒤에 사유를 말로 옮기는 것**이라 여기 있어도 된다:
   * 쓸 수 있는지는 언제나 `view.canUseMagazine` 하나가 정하고, 이 함수는 그것이 거짓일 때만 불린다.
   */
  function shotsUntilMagazineFits(view: GameHudView): number | null {
    const perMagazine = view.ammoPerMagazine
    if (perMagazine === undefined || perMagazine <= 0) return null
    // 잔탄이 상한을 넘는 일은 없지만(`ammoCapacity`), 음수가 나오면 문장이 거짓이 되므로 막는다.
    const room = Math.max(0, view.ammoMax - view.ammo)
    return Math.max(1, perMagazine - room)
  }

  /**
   * 탄창 칩을 눌렀다.
   *
   * **언제나 이벤트를 보낸다.** 눌렀는데 조용한 버튼은 고장 난 버튼이고, 잔탄이 마른 상황에서
   * 그 오해는 곧 판을 버리는 이유가 된다. 못 쓰는 상황이면 리듀서가 막힌 소리를 내고,
   * 이유는 화면이 한 줄로 말한다.
   *
   * ## 분기가 규칙과 어긋나 있던 자리다 (2026-09-02 수정)
   *
   * 규칙이 "잔탄이 가득이면 거부"에서 **"빈 자리가 탄창 하나보다 작으면 거부"**로 바뀌었는데
   * (`gameState.hasRoomForFullMagazine`), 여기는 `ammo >= ammoMax`만 보고 있었다. 기본값에서
   * 잔탄 11~15는 어느 분기에도 걸리지 않아 **소리만 나고 아무 말도 안 나왔다** - 눌렀는데
   * 조용한 버튼을 만들지 않겠다는 이 함수의 약속이 그 구간에서만 깨져 있었다.
   *
   * 지금은 남은 사유가 둘뿐이고 둘 다 말한다. (보유량 0과 수색 밖은 버튼이 아예 비활성이라
   * 여기까지 오지 않는다.) 자리 부족을 한 문장으로 합친 이유는 규칙이 하나이기 때문이다 -
   * 잔탄 16도 잔탄 15도 같은 이유로 거부되므로, 문장을 둘로 나누면 규칙이 둘인 것처럼 읽힌다.
   *
   * ## 뷰를 **핸들러보다 먼저** 붙잡는 이유 (2026-09-02 수정)
   *
   * `session.useMagazine()`은 `send()` 뒤에 `render()`를 **동기로** 부른다. 그래서 핸들러가
   * 돌아온 뒤의 `lastView`는 이미 **소비 이후의** 뷰다. 재장전에 성공하면 잔탄이 상한에 닿아
   * 그 뷰의 `canUseMagazine`은 언제나 거짓이고, 그것으로 판단하면 **성공할 때마다 거부 사유가
   * 뜬다** - "+6발"과 "6발 더 쓰면 탄창을 넣을 수 있다"가 같은 순간에 겹쳐 뜨고 서로를 부정한다.
   *
   * 사람이 이 규칙을 배우는 경로가 그 한 줄뿐이므로(`render`의 주석) 거기서 거짓말을 하면
   * 규칙을 잘못 배운다. 판단의 근거는 언제나 **누른 순간에 화면에 있던 값**이다.
   */
  function onMagPress(): void {
    // 순서가 규약이다. 핸들러가 세션을 다시 그리기 전의 뷰라야 "눌렀을 때"의 사정을 말한다.
    const view = lastView
    options.handlers.onUseMagazine()

    if (view === null || view.canUseMagazine) return

    if (view.magazineUsesLeft <= 0) {
      showNotice(t('hud.mag.roundSpent'), 'warn')
      return
    }

    // 쓸 수 있는데 못 썼다면 남은 사유는 자리뿐이다. 상태가 아니라 할 일을 말한다.
    const shots = shotsUntilMagazineFits(view)
    showNotice(
      shots === null ? t('hud.mag.needRoom') : t('hud.mag.needRoomCount', { count: shots }),
      'warn',
    )
  }

  /*
   * 발견 수가 **늘어난** 순간에만 카운터를 튕긴다.
   * 탭 지점의 히트마커는 "맞았다"를 말하지만 "몇 개 남았다"는 말해 주지 않는다.
   * 두 연출을 같은 프레임에 붙여야 시선이 진행도의 위치를 학습한다.
   * 줄어드는 경우(= 새 라운드 시작)는 튕기지 않는다 - 시작을 성취로 보여 줄 이유가 없다.
   */
  let lastFoundCount = -1

  /**
   * 콤보가 **오른 순간에만** 칩을 튕긴다. 발견 카운터의 `bumpOnIncrease`와 같은 수법이며
   * 같은 프레임에 둘이 함께 튕겨야 "이 발견이 저 배수를 만들었다"가 설명 없이 읽힌다.
   *
   * 내려갈 때(끊김·새 라운드)는 튕기지 않는다 - 잃은 것을 성취처럼 보여 줄 이유가 없고,
   * 끊김의 연출은 맞은 지점이 이미 맡고 있다(`ComboChip` 주석).
   */
  let lastCombo = -1

  function bumpCombo(combo: number, visible: boolean): void {
    if (combo === lastCombo) return
    const isIncrease = lastCombo >= 0 && combo > lastCombo
    lastCombo = combo

    found.combo.root.classList.remove('is-bump')
    if (!isIncrease || !visible) return

    restartAnimation(found.combo.root)
    found.combo.root.classList.add('is-bump')
  }

  function bumpOnIncrease(foundCount: number): void {
    if (foundCount === lastFoundCount) return
    const isIncrease = lastFoundCount >= 0 && foundCount > lastFoundCount
    lastFoundCount = foundCount

    // 끝난 애니메이션의 클래스를 남겨 두지 않는다. 남은 클래스는 눈에 보이지 않지만
    // "지금 상태"를 읽는 수단(테스트·디버깅)을 거짓말하게 만든다.
    found.value.classList.remove('is-bump')
    if (!isIncrease) return

    restartAnimation(found.value)
    found.value.classList.add('is-bump')
  }

  /*
   * ## 재장전을 이벤트가 아니라 **뷰의 변화**로 잡는 이유
   *
   * 정답 방 리필도 월드 아이템도 사라진 지금 **수색 중에 탄환이 느는 경로는 탄창 하나뿐이다**
   * (`gameState.reduce`에서 `ammo`를 늘리는 곳은 `onUseMagazine`이 유일하다). 그러니 같은
   * 페이즈 안에서 잔탄이 늘었다면 그건 탄창을 썼다는 뜻이고, 늘어난 양이 곧 실제 회복량이다.
   * 상한에 걸려 `ammoPerMagazine`을 다 못 받은 경우까지 저절로 정확해진다 - 요청값이 아니라
   * 화면에 실제로 찍힌 값의 차이를 보기 때문이다.
   *
   * `magazineUsed` 효과를 받는 핸들을 파지 않은 것은 그것을 불러 줄 파일(`session.ts`)이
   * 다른 담당 소유라서다. 뷰에서 파생시키면 **통합 쪽이 한 줄도 고치지 않아도 연출이 붙는다.**
   * 발견 카운터의 `bumpOnIncrease`가 이미 같은 수법이라 규칙도 하나 늘지 않는다.
   *
   * 페이즈가 같은 프레임끼리만 비교하는 것이 이 함수의 유일한 방어선이다. 라운드가 다시 서면
   * 잔탄이 상한으로 되돌아가는데(`enterHunt`), 그 전이는 언제나 `watch → hunt`라 여기 걸리지 않는다.
   * "16발을 채웠다"는 자막이 라운드 시작마다 뜨는 것이 이 가드가 막는 유일한 사고다.
   */
  let lastAmmo = -1
  let lastPhase: HudPhase | null = null

  function ammoGainSince(view: GameHudView): number {
    const samePhase = lastPhase === view.phase
    const gained = samePhase && lastAmmo >= 0 && view.ammo > lastAmmo ? view.ammo - lastAmmo : 0
    lastAmmo = view.ammo
    /*
     * 페이즈가 바뀌면 상단·하단 줄의 높이가 바뀐다 - 건너뛰기 버튼의 이름이 갈리고 원래 방 버튼이 섰다 사라진다.
     * 다시 재라고 표시만 한다. 실제로 재는 것은 그 문구를 다 칠한 뒤인 `render`의 끝이다.
     */
    if (!samePhase) sizesDirty = true
    lastPhase = view.phase
    return gained
  }

  function flashAmmoGain(amount: number, ammoLeft: number): void {
    clearGain()
    setText(ammo.gain, t('hud.ammoGain.label', { amount }))

    restartAnimation(ammo.gain)
    ammo.gain.classList.add('is-on')
    ammo.value.classList.add('is-bump')
    // 탄피 줄이 왼쪽부터 차오른다. 자막이 "얼마나"를 말한다면 이쪽은 "차올랐다"를 말한다.
    ammo.pips.classList.add('is-refill')

    /*
     * 타이머로 끄는 이유는 `prefers-reduced-motion`이다. 움직임을 끈 환경에서는 자막이
     * 애니메이션 없이 그냥 떠 있으므로(CSS 참고), 누군가는 그것을 치워야 한다.
     * 애니메이션 종료에 기대면 그 환경에서 자막이 영영 남는다.
     */
    gainTimer = window.setTimeout(clearGain, AMMO_GAIN_DURATION_MS)

    // 시각 연출은 스크린리더에 아무것도 남기지 않는다. 사격과 같은 라이브 리전으로 보낸다.
    setText(announcer, t('hud.ammoGain.announce', { amount, ammo: ammoLeft }))
  }

  return {
    root,

    render(view: GameHudView): void {
      lastView = view

      /* 페이즈 · 시계 */
      /*
       * 페이즈를 DOM에 적어 둔다. 쓰는 곳은 화면 계측기(`app/coverProbe.ts`)다 - 씬을 덮어도 되는 넓이가
       * 페이즈마다 다른데(외우기·수색은 방을 봐야 하므로 30%), 그것을 화면의 **문구**로 알아내면 언어가 바뀌는
       * 순간 계측기가 조용히 거짓 초록을 낸다. CSS는 이 값을 읽지 않는다 - 생김새의 원본은 클래스 쪽이다.
       *
       * `dataset` 세터가 아니라 `setAttr`인 이유는 이 함수가 매 프레임 돌기 때문이다 - `dataset`은 값이
       * 같아도 언제나 `setAttribute`를 부른다(`ui/dom.ts`의 규약: 같은 값이면 DOM을 건드리지 않는다).
       */
      setAttr(root, 'data-phase', view.phase)
      // 원래 방에서는 보고 있는 것이 현장이 아니라 원본이다. 문구가 바뀌고 CSS가 점을 ◂ 삼각형으로 바꾼다.
      setText(phase.rec, t(view.isInOriginRoom ? 'hud.tag.origin' : 'hud.tag.scene'))
      setText(phase.name, phaseName(view.phase))
      /*
       * 내 구획 이름표(M11). 라운드 내내 서 있다 - 바꾸기에서는 "여기만 고친다"이고 수색에서는 "여기는 내가 못 찾는다"라
       * 같은 이름표가 두 문장을 대신한다. 이름이 없으면(싱글 · 구획 없는 판 · 사전에 없는 id) 접는다.
       */
      const zoneText = zoneName(view.myZoneId)
      setText(phase.zone, zoneText ?? '')
      setAttr(phase.zone, 'aria-label', zoneText === undefined ? '' : t('zone.mineAria', { name: zoneText }))
      setHidden(phase.zone, zoneText === undefined)
      // 시간으로 끝나지 않는 페이즈(결과)는 시계를 비운다 - 결과 자막판이 화면을 덮고 있어 보이지도 않는다.
      setText(phase.clock, view.phaseRemainingMs === null ? '' : formatClock(view.phaseRemainingMs))
      setText(phase.hint, view.isInOriginRoom ? t('hud.originHint') : phaseHint(view.phase))
      // 다급함은 **수색에서만** 표시한다. 외우기 60초는 절반이 30초 미만이라 항상 붉어지고,
      // 리빌은 길이가 미발견 수에 비례해 대개 30초 미만이다. 늘 켜져 있는 경고는 경고가 아니다.
      phase.root.classList.toggle(
        'is-urgent',
        view.phase === 'hunt' &&
          view.phaseRemainingMs !== null &&
          view.phaseRemainingMs <= URGENT_PHASE_MS,
      )

      /*
       * 넘어가기. 여럿이 판에서는 **표**이므로 몇이 눌렀는지를 버튼이 말한다(로드맵 - 남이 눌러도 아무 일이 없던 자리).
       * 혼자인 판에서는 세지 않는다 - "1/1"은 셈이 아니라 잡음이다.
       */
      const skipText = phaseSkip(view.phase)
      const counting = view.skip.needed > 1
      setText(skip, counting ? t('phase.skip.votes', { text: skipText, votes: view.skip.votes, needed: view.skip.needed }) : skipText)
      setFlag(skip, 'is-voted', counting && view.skip.mine)
      setAttr(skip, 'aria-pressed', counting ? (view.skip.mine ? 'true' : 'false') : 'false')
      skip.hidden = skipText === ''

      /* 발견 카운터 */
      bumpOnIncrease(view.foundCount)
      setText(found.value, String(view.foundCount))
      setText(found.total, `/ ${view.changeCount}`)
      setBarRatio(found.fill, view.changeCount > 0 ? view.foundCount / view.changeCount : 0)
      found.root.classList.toggle('is-complete', view.changeCount > 0 && view.foundCount >= view.changeCount)

      /*
       * 콤보 칩. **수색 중에만 뜬다.**
       *
       * 리빌·결과에서는 콤보가 더 이상 움직이지 않으므로 상시 표시가 할 말이 없고,
       * 그때 화면에 필요한 것은 정답 훑기와 자막판이다. 최종 성적으로서의 최고 콤보는
       * 결과 화면이 따로 말한다(`resultScreen`의 최고 연속 타일).
       */
      const showCombo = view.phase === 'hunt' && view.combo >= COMBO_CHIP_MIN
      bumpCombo(view.combo, showCombo)
      setText(
        found.combo.label,
        t(view.comboAtCap ? 'hud.combo.labelCap' : 'hud.combo.label', { count: view.combo }),
      )
      // 표기는 연출 레이어와 **같은 함수**를 쓴다(파일 상단 import 주석).
      setText(found.combo.value, formatMultiplier(view.comboMultiplier))
      found.combo.root.classList.toggle('is-on', showCombo)
      found.combo.root.classList.toggle('is-cap', view.comboAtCap)
      // 걸기 칩(M7). 뷰의 불리언을 그대로 넘긴다 - 조건은 `raiseChip.ts`가 아니라 규칙이 안다. 수색 중 쏘는 좌석에만 선다(`raiseVisible`).
      found.raise.render({
        raised: view.raised,
        canRaise: view.canRaise,
        active: view.phase === 'hunt' && view.raiseVisible,
      })

      /* 탄환 - 컨셉시트 4.3의 "대형 표시 + 잔탄 경고" + 이번 개편의 보급 피드백 */
      const gained = ammoGainSince(view)
      if (gained > 0) flashAmmoGain(gained, view.ammo)

      setText(ammo.value, String(view.ammo))

      // 상한이 곧 `ammoMax`라 칸 수는 하나로 정해진다. 담을 수 없는 폭이면 줄째 감춘다.
      const showPips = view.ammoMax > 0 && view.ammoMax <= AMMO_PIP_MAX
      ammo.pips.hidden = !showPips
      if (showPips) syncPips(ammo.pips, view.ammoMax, view.ammo)

      const tier = ammoTier(view)
      for (const state of AMMO_TIERS) {
        ammo.root.classList.toggle(state, state === `is-${tier}`)
      }
      ammo.root.classList.toggle('is-empty', view.ammo <= 0)
      ammo.root.classList.toggle('is-idle', view.phase !== 'hunt' || view.isInOriginRoom)

      /* 탄창 칩 */
      setText(ammo.mag.count, String(view.magazines))
      const mag = magState(view, tier)
      for (const state of MAG_STATES) {
        ammo.mag.root.classList.toggle(state, state === mag)
      }
      /*
       * 누를 수 없게 막는 것은 **누를 이유가 아예 없는 두 경우뿐**이다 - 가진 것이 0이거나,
       * 수색 중이 아니거나. 나머지 거부(잔탄 가득·이번 판 상한)는 막지 않고 눌리게 둔 뒤
       * 이유를 말한다(`onMagPress`). 규칙을 배우는 유일한 경로가 그 한 줄이기 때문이다.
       * 정답 방 안에서는 막지 않는다 - 재장전은 방에 손대는 일이 아니라 HUD의 일이다.
       */
      ammo.mag.root.disabled = view.magazines <= 0 || view.phase !== 'hunt'
      setAttr(ammo.mag.root, 'aria-label', t('hud.mag.aria', { count: view.magazines }))

      /*
       * 안내 한 줄. **탄창이 있으면 문장이 행동으로 바뀐다** -
       * "얼마 안 남았다"(상태)보다 "탄창을 써라"(할 일)가 이 국면에서 할 수 있는 말이다.
       * 반대로 `low`는 이제 정말로 끝난 상태이므로 문장도 끝을 말한다.
       */
      const ammoHint = view.isInOriginRoom
        ? t('hud.ammoBlocked')
        : tier === 'reload'
          ? t('hud.mag.reload')
          : tier !== 'low'
            ? ''
            : view.ammo > 0
              ? t('hud.ammoLow')
              : // 가진 것은 있는데 이번 판에 못 쓰는 상황과 아예 없는 상황은 대처가 다르다.
                t(view.magazines > 0 ? 'hud.mag.roundSpent' : 'hud.ammoEmpty')
      setText(ammo.hint, ammoHint)

      /* 정답 방 버튼 */
      const face = describeOrigin(view.origin)
      setText(origin.label, face.label)
      setText(origin.sub, face.sub)
      setBarRatio(origin.fill, face.ratio)
      origin.root.disabled = face.disabled
      origin.root.hidden = face.hidden
      origin.root.classList.toggle('is-urgent', face.urgent)
      origin.root.classList.toggle('is-brief', face.brief)
      for (const state of ORIGIN_STATES) {
        origin.root.classList.toggle(state, state === face.state)
      }

      /* 원본 방 전역 표식 */
      root.classList.toggle('is-origin', view.isInOriginRoom)
      root.classList.toggle('is-origin-urgent', view.isInOriginRoom && face.urgent)
      root.classList.toggle('is-result', view.phase === 'result')

      /*
       * 이 줄들의 크기를 남들이 읽는다(`overlayLayout.ts` - 위 기둥의 시작 높이 · 문 앞 줄의 오른쪽 · 카메라 묶음의 높이).
       * 문구를 전부 칠한 **뒤**에 재고, 바뀔 이유가 생긴 프레임에만 잰다 - 매 프레임 재면 레이아웃을 매 프레임 강제한다.
       */
      if (sizesDirty) {
        sizesDirty = false
        for (const size of sizes) size.refresh()
      }
    },

    flashShot(clientX: number, clientY: number, kind: ShotMarkKind, label?: string): void {
      clearMark()

      mark.style.left = `${clientX}px`
      mark.style.top = `${clientY}px`
      setText(markLabel, label ?? t(`shot.${kind}.label`))

      restartAnimation(mark)
      mark.className = `gh-mark is-on gh-mark--${kind}`
      markTimer = window.setTimeout(clearMark, SHOT_MARK_DURATION_MS)

      if (kind === 'miss' || kind === 'dry') {
        clearDamage()
        restartAnimation(damage)
        damage.classList.add('is-on')
        damageTimer = window.setTimeout(clearDamage, MISS_FLASH_DURATION_MS)
      }

      // 화면 문구와 소리 문구를 따로 두는 이유는 "-1발"이 소리로는 불친절해서다.
      setText(announcer, t(`shot.${kind}.announce`))
    },

    showNotice,

    setVisible(visible: boolean): void {
      // 숨길 때 진행 중인 연출도 끈다. 안 그러면 모드를 오갔을 때
      // 몇 초 전에 쏜 히트마커가 아직 떠 있는 유령 상태가 된다.
      if (!visible) {
        clearMark()
        clearDamage()
        clearNotice()
        clearGain()
        /*
         * 잔탄 기준선도 함께 버린다. 배치 모드를 다녀오는 동안 라운드가 통째로 갈릴 수 있고,
         * 옛 잔탄과 새 잔탄을 비교하면 다시 켜는 첫 프레임에 있지도 않은 보급 자막이 뜬다.
         */
        lastAmmo = -1
        lastPhase = null
        // 콤보 기준선도 같은 이유로 버린다. 배치 모드를 다녀오는 사이 라운드가 갈리면
        // 지난 판의 콤보와 새 판의 0을 비교하게 되고, 그건 비교가 아니라 잡음이다.
        lastCombo = -1
        found.combo.root.classList.remove('is-bump', 'is-on')
        // 마지막 뷰도 버린다. 다시 켜기 전에 칩을 누를 수는 없지만, 남겨 두면 다음 판의
        // 첫 프레임 전까지 지난 판의 수치로 이유를 설명하는 창이 생긴다.
        lastView = null
      }
      root.hidden = !visible
      // 접힌 상자의 실측치는 관측기가 놓칠 수 있다. 남은 높이는 그만큼의 영원한 빈칸이 되므로 즉시 다시 잰다
      // (접을 때는 다음 `render`가 오지 않을 수 있어 여기서 미루면 값이 영영 낡는다).
      sizesDirty = true
      for (const size of sizes) size.refresh()
    },

    dispose(): void {
      clearMark()
      clearDamage()
      clearNotice()
      clearGain()
      stopLocaleWatch()
      skip.removeEventListener('click', options.handlers.onSkipPhase)
      origin.root.removeEventListener('click', options.handlers.onToggleOrigin)
      found.raise.dispose()
      for (const size of sizes) size.dispose()
      // 칩의 청취자는 화살표라 참조가 여기 없다. 노드째 버리는 것으로 정리가 끝난다.
      root.remove()
      // 자막은 루트 밖(위 기둥)에 산다. 루트만 버리면 지난 판의 자막이 다음 판까지 남는다.
      // `remove()`가 아니라 `unplace()`인 이유는 기둥의 관측기까지 함께 끊어야 하기 때문이다.
      layout.unplace(notice)
    },
  }
}
